/**
 * Influencer Picks
 * - Play behavior for uploaded video + YouTube/Vimeo embeds
 * - AJAX add-to-cart with best-effort cart UI updates
 */

(function () {
  'use strict';

  function qsAll(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (_e) {
      return null;
    }
  }

  async function addToCart(form) {
    const baseUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
    const url = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;
    const formData = new FormData(form);

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });

    const data = await res.json().catch(() => null);
    if (!res.ok || (data && data.status)) {
      throw new Error((data && (data.description || data.message)) || 'Failed to add to cart');
    }
    return data;
  }

  function tryUpdateCartUI(cartData) {
    if (!cartData) return;

    // Drawer/notification (best-effort)
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') cartDrawer.renderContents(cartData);
      if (cartNotification && typeof cartNotification.renderContents === 'function') cartNotification.renderContents(cartData);
    } catch (_e) {}

    // Count bubble (best-effort)
    if (cartData.item_count !== undefined) {
      qsAll('.cart-count-bubble, [data-cart-count]').forEach((el) => {
        el.textContent = cartData.item_count;
      });
    }

    // Pub/Sub (best-effort)
    try {
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'influencer-picks', cartData });
      }
    } catch (_e) {}
  }

  function initVideoPlay(root) {
    const cards = qsAll('.influencer-picks [data-ip-video]', root);
    const track = root.querySelector('.influencer-picks__track');
    const isMobile = window.innerWidth <= 749;

    cards.forEach((card, index) => {
      const btn = card.querySelector('[data-ip-play]');
      if (!btn) return;
      if (btn.dataset.ipBound === '1') return;
      btn.dataset.ipBound = '1';

      const playNow = () => {
        const inner = card.querySelector('.influencer-picks__video-inner');
        if (!inner) return;

        // Uploaded video
        const video = inner.querySelector('video');
        if (video) {
          video.play().catch(() => {});
          btn.setAttribute('aria-hidden', 'true');
          btn.style.opacity = '0';
          btn.style.pointerEvents = 'none';
          return;
        }

        // Embed
        const thumb = inner.querySelector('[data-ip-thumb]');
        const iframeWrap = inner.querySelector('[data-ip-iframe]');
        if (thumb && iframeWrap) {
          thumb.style.display = 'none';
          iframeWrap.hidden = false;
        }
      };

      btn.addEventListener('click', playNow);

      // Autoplay (muted) when card enters viewport
      const section = card.closest('.influencer-picks');
      const shouldAutoplay = section && section.dataset && section.dataset.ipAutoplay === 'true';
      if (!shouldAutoplay) return;

      const markPlayed = () => { card.dataset.ipPlayed = '1'; };

      // If already playing/played, skip
      if (card.dataset.ipPlayed === '1') return;

      if ('IntersectionObserver' in window) {
        // Delay observer setup on mobile to ensure scroll is reset first
        const setupObserver = () => {
          // On mobile, only observe if scroll is at 0 (to prevent triggering on wrong card)
          if (isMobile && track && track.scrollLeft !== 0) {
            // If scroll is not at 0, wait a bit and check again
            setTimeout(setupObserver, 100);
            return;
          }
          
          const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (card.dataset.ipPlayed === '1') return;
              
              // On mobile, double-check that we're at scroll position 0 before playing first card
              if (isMobile && index === 0 && track && track.scrollLeft !== 0) {
                return; // Don't play if scroll is not at start
              }
              
              markPlayed();
              playNow();
              io.disconnect();
            });
          }, { threshold: 0.55 });
          io.observe(card);
        };
        
        // Delay observer setup on mobile to let scroll reset happen first
        if (isMobile) {
          setTimeout(setupObserver, 500);
        } else {
          setupObserver();
        }
      } else {
        // Fallback: try once on load
        setTimeout(() => {
          if (card.dataset.ipPlayed === '1') return;
          markPlayed();
          playNow();
        }, 400);
      }
    });
  }

  function initATC(root) {
    const forms = qsAll('.influencer-picks [data-ip-atc]', root);

    forms.forEach((form) => {
      if (form.dataset.ipBound === '1') return;
      form.dataset.ipBound = '1';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('.influencer-picks__add');
        const textEl = form.querySelector('.influencer-picks__add-text');
        if (!btn) return;
        if (btn.disabled) return;

        const original = textEl ? textEl.textContent : 'ADD';
        try {
          btn.disabled = true;
          if (textEl) textEl.textContent = 'ADDING';

          const addResponse = await addToCart(form);
          if (textEl) textEl.textContent = 'ADDED';
          setTimeout(() => {
            if (textEl) textEl.textContent = original;
          }, 1200);

          const cart = await fetchCart();
          tryUpdateCartUI(cart || addResponse);
        } catch (_err) {
          if (textEl) textEl.textContent = 'ERROR';
          setTimeout(() => {
            if (textEl) textEl.textContent = original;
          }, 1200);

          try {
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'influencer-picks', message: 'Failed to add to cart' });
            }
          } catch (_e) {}
        } finally {
          setTimeout(() => {
            btn.disabled = false;
          }, 250);
        }
      });
    });
  }

  function resetTrackScroll(track) {
    if (!track) return;
    
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 749;
    if (!isMobile) return;
    
    // Disable scroll-snap initially to prevent snapping during reset
    track.style.scrollSnapType = 'none';
    
    // Force scroll to absolute 0 - direct assignment
    const resetScroll = () => {
      // Set scrollLeft to 0 first (most reliable)
      track.scrollLeft = 0;
      // Also use scrollTo for better browser compatibility
      if (track.scrollTo) {
        track.scrollTo({ left: 0, behavior: 'auto' });
      }
    };
    
    // Immediate reset
    resetScroll();
    
    // Reset after paint (multiple times to ensure it sticks)
    requestAnimationFrame(() => {
      resetScroll();
      requestAnimationFrame(() => {
        resetScroll();
        // Mark as initialized and re-enable scroll-snap after scroll is set
        track.setAttribute('data-scroll-init', 'true');
        track.style.scrollSnapType = '';
      });
    });
    
    // Reset after short delay
    setTimeout(() => {
      resetScroll();
      track.setAttribute('data-scroll-init', 'true');
      track.style.scrollSnapType = '';
    }, 10);
    
    // Reset after longer delay to catch any late scrolls
    setTimeout(() => {
      resetScroll();
      track.setAttribute('data-scroll-init', 'true');
      track.style.scrollSnapType = '';
    }, 100);
    
    // Final reset after everything settles
    setTimeout(() => {
      resetScroll();
      track.setAttribute('data-scroll-init', 'true');
      track.style.scrollSnapType = '';
    }, 300);
    
    // One more reset after a longer delay
    setTimeout(() => {
      resetScroll();
      track.setAttribute('data-scroll-init', 'true');
      track.style.scrollSnapType = '';
    }, 500);
    
    // Watch for scroll changes and reset if needed (only for first 3 seconds)
    // This catches any scroll that happens before our resets take effect
    let scrollWatchTimeout;
    let watchCount = 0;
    const maxWatchCount = 30; // Watch for 3 seconds (30 * 100ms)
    
    const watchScroll = () => {
      watchCount++;
      // If scroll is not at 0, reset it (but only for first few seconds)
      if (track.scrollLeft !== 0 && watchCount < maxWatchCount) {
        track.scrollTo({ left: 0, behavior: 'auto' });
        track.scrollLeft = 0;
      }
    };
    
    const startWatching = () => {
      watchCount = 0;
      track.addEventListener('scroll', watchScroll, { passive: true });
      clearTimeout(scrollWatchTimeout);
      scrollWatchTimeout = setTimeout(() => {
        track.removeEventListener('scroll', watchScroll);
      }, 3000);
    };
    
    startWatching();
    setTimeout(startWatching, 100);
    setTimeout(startWatching, 500);
  }

  function init(root) {
    // Reset scroll position FIRST, before video play logic
    const tracks = qsAll('.influencer-picks__track', root);
    const isMobile = window.innerWidth <= 749;
    
    tracks.forEach((track) => {
      if (isMobile) {
        // Aggressive reset for mobile
        track.scrollLeft = 0;
        if (track.scrollTo) {
          track.scrollTo({ left: 0, behavior: 'auto' });
        }
        // Disable scroll-snap temporarily
        track.style.scrollSnapType = 'none';
        // Force reset multiple times
        setTimeout(() => {
          track.scrollLeft = 0;
          track.style.scrollSnapType = '';
        }, 0);
        setTimeout(() => {
          track.scrollLeft = 0;
          track.style.scrollSnapType = '';
        }, 50);
      }
      resetTrackScroll(track);
    });
    
    // Then initialize video play and ATC
    initVideoPlay(root);
    initATC(root);
    
    // Also reset on window load and resize
    if (isMobile) {
      const resetOnLoad = () => {
        tracks.forEach((track) => {
          track.scrollLeft = 0;
          if (track.scrollTo) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          }
          resetTrackScroll(track);
        });
      };
      
      if (document.readyState === 'complete') {
        setTimeout(resetOnLoad, 0);
      } else {
        window.addEventListener('load', resetOnLoad, { once: true });
      }
      
      // Also reset on resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (window.innerWidth <= 749) {
            tracks.forEach((track) => {
              track.scrollLeft = 0;
            });
          }
        }, 100);
      });
    }
  }

  // Immediate reset for any existing tracks (before DOMContentLoaded)
  if (window.innerWidth <= 749) {
    const tracks = document.querySelectorAll('.influencer-picks__track');
    tracks.forEach((track) => {
      // Force scroll to 0 immediately
      track.scrollLeft = 0;
      // Also use scrollTo for better browser support
      if (track.scrollTo) {
        track.scrollTo({ left: 0, behavior: 'auto' });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const sectionId = event && event.detail && event.detail.sectionId;
      const root = sectionId ? document.getElementById(`influencer-picks-${sectionId}`) : document;
      init(root || document);
    });
  }
})();
